import { Injectable } from '@angular/core';
import {
  GroupedTpaCoverage,
  InsuranceTypeGroup,
  PersonGroup,
  PlanGroup,
  PolicyGroup,
} from '../models/tpa-group.model';
import { TpaSearchCoverageResExtended } from '../models/http/tpa-http.model';

@Injectable({
  providedIn: 'root',
})
export class TpaGroupSortService {
  sortGroupedData(groupedData: GroupedTpaCoverage): GroupedTpaCoverage {
    if (!groupedData?.persons?.length) {
      return groupedData;
    }

    groupedData.persons.sort(this.comparePersons);

    groupedData.persons.forEach((person) => {
      person.insuranceTypes.sort(this.compareInsuranceTypes);

      person.insuranceTypes.forEach((insuranceType) => {
        insuranceType.policies.sort(this.comparePolicies);

        insuranceType.policies.forEach((policy) => {
          policy.plans.sort(this.comparePlans);

          policy.plans.forEach((plan) => {
            plan.items.sort(this.compareItems);
          });
        });
      });
    });

    return groupedData;
  }

  compareByStatus(
    aIsActive: boolean,
    aHasWaiting: boolean,
    bIsActive: boolean,
    bHasWaiting: boolean,
  ): number {
    const aIsInactive = !aIsActive && !aHasWaiting;
    const bIsInactive = !bIsActive && !bHasWaiting;

    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    if (aIsInactive && bHasWaiting) return -1;
    if (aHasWaiting && bIsInactive) return 1;

    return 0;
  }

  private hasActiveInsurance(person: PersonGroup): boolean {
    return person.insuranceTypes.some((it) =>
      it.policies.some((p) => p.plans.some((pl) => pl.hasActive)),
    );
  }

  private hasWaitingInsurance(person: PersonGroup): boolean {
    return person.insuranceTypes.some((it) =>
      it.policies.some((p) => p.plans.some((pl) => pl.hasWaiting)),
    );
  }

  private comparePersons = (a: PersonGroup, b: PersonGroup): number => {
    const aHasActive = this.hasActiveInsurance(a);
    const bHasActive = this.hasActiveInsurance(b);

    const aHasWaiting = this.hasWaitingInsurance(a);
    const bHasWaiting = this.hasWaitingInsurance(b);

    const aIsActive = aHasActive && !aHasWaiting;
    const bIsActive = bHasActive && !bHasWaiting;

    const statusComparison = this.compareByStatus(aIsActive, aHasWaiting, bIsActive, bHasWaiting);
    if (statusComparison !== 0) return statusComparison;

    if (a.dateOfBirth && b.dateOfBirth) {
      return new Date(b.dateOfBirth).getTime() - new Date(a.dateOfBirth).getTime();
    }

    return 0;
  };

  private hasActiveInsuranceType(insuranceType: InsuranceTypeGroup): boolean {
    return insuranceType.policies.some((p) => p.plans.some((pl) => pl.hasActive));
  }

  private hasWaitingInsuranceType(insuranceType: InsuranceTypeGroup): boolean {
    return insuranceType.policies.some((p) => p.plans.some((pl) => pl.hasWaiting));
  }

  private compareInsuranceTypes = (a: InsuranceTypeGroup, b: InsuranceTypeGroup): number => {
    const aHasActive = this.hasActiveInsuranceType(a);
    const bHasActive = this.hasActiveInsuranceType(b);

    const aHasWaiting = this.hasWaitingInsuranceType(a);
    const bHasWaiting = this.hasWaitingInsuranceType(b);

    const aIsActive = aHasActive && !aHasWaiting;
    const bIsActive = bHasActive && !bHasWaiting;

    const statusComparison = this.compareByStatus(aIsActive, aHasWaiting, bIsActive, bHasWaiting);
    if (statusComparison !== 0) return statusComparison;

    return (a.insuranceTypeName || '').localeCompare(b.insuranceTypeName || '');
  };

  private hasActivePolicy(policy: PolicyGroup): boolean {
    return policy.plans.some((pl) => pl.hasActive);
  }

  private hasWaitingPolicy(policy: PolicyGroup): boolean {
    return policy.plans.some((pl) => pl.hasWaiting);
  }

  private comparePolicies = (a: PolicyGroup, b: PolicyGroup): number => {
    // Primary sort: by policyStartDate descending (most recent at top)
    if (a.policyStartDate && b.policyStartDate) {
      return new Date(b.policyStartDate).getTime() - new Date(a.policyStartDate).getTime();
    }

    return 0;
  };

  private comparePlans = (a: PlanGroup, b: PlanGroup): number => {
    // Primary sort: by planEndYear descending (most recent at top)
    if (a.planEndYear && b.planEndYear) {
      return b.planEndYear.localeCompare(a.planEndYear);
    }

    // Secondary sort: by planStartYear descending if planEndYear is the same
    if (a.planStartYear && b.planStartYear) {
      return b.planStartYear.localeCompare(a.planStartYear);
    }

    return 0;
  };

  private compareItems = (
    a: TpaSearchCoverageResExtended,
    b: TpaSearchCoverageResExtended,
  ): number => {
    if (a.dateJoined && b.dateJoined) {
      return new Date(b.dateJoined).getTime() - new Date(a.dateJoined).getTime();
    }
    return 0;
  };
}
